import React from 'react';

import waveLengthRange from '@client/config/waveLengthRange';
import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import { SpectralRanges, SpectrogramPoint } from '@ledium/types';
import { computeSpectralSimilarity } from '@ledium/ui-common';
import { Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';

import { MixerSettings } from './SpectrumMixerChannelSettingsItem';

const Root = styled('div')(
    ({ theme }) => `
  td,
  th {
    padding: 0px 10px;
  }

  th {
    text-align: center;
  }

  tr:nth-of-type(2) th:first-of-type {
    text-align: left;
  }

  td {
    text-align: right;
  }

  tr td:first-of-type {
    text-align: left;
  }

  .darker {
    background: ${theme.palette.action.hover};
  }

  .small {
    font-size: 12px;
    padding-left: 0;
  }

  .bold {
    font-weight: bold;
  }

  .center {
    text-align: center;
  }

  th,
  tbody tr:nth-of-type(2) td,
  tbody tr:nth-of-type(8) td {
    border-bottom: 1px solid ${theme.palette.grey[500]};
  }
`
);

const rangesMap: Record<string, string> = {};
for (const e of waveLengthRange.ranges) {
    rangesMap[e.name] = `${Math.round(e.from / 10) * 10} - ${Math.round(e.to / 10) * 10}nm`;
}

type RangeKey = keyof SpectralRanges;
const spectrumOrder: RangeKey[] = ['sum', 'visibleSum', 'uv', 'blue', 'green', 'red', 'fr', 'ir'];

interface SpectralRangesMap {
    pfd: SpectralRanges;
    irradiance: SpectralRanges;
}

const SpectrumMixerDetailedIntensityInfo = () => {
    const {
        pageState: { formData }
    } = usePageStore();
    const settings = formData.settings as MixerSettings;
    const unit = settings.intensityUnit;
    const intensityRanges = formData.intensityRanges as SpectralRangesMap | null;
    const lightRecipe = formData.lightRecipe as { spectrogramRanges: SpectralRangesMap; spectrogram?: SpectrogramPoint[] } | null;

    const mixRanges = intensityRanges && intensityRanges[unit];
    const recipeRanges = lightRecipe?.spectrogramRanges && lightRecipe?.spectrogramRanges[unit];

    const aggregatedSpectrogram = formData.aggregatedSpectrogram as SpectrogramPoint[] | null;
    const recipeSpectrogram = lightRecipe?.spectrogram ?? null;
    const similarity = aggregatedSpectrogram && recipeSpectrogram ? computeSpectralSimilarity(recipeSpectrogram, aggregatedSpectrogram, unit) : null;

    if (!mixRanges || !recipeRanges) {
        return null;
    }

    return (
        <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
            <Root>
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell colSpan={5}>
                                    {i18n(unit)} ({i18n(unit + '_unit')})
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>{i18n('detail_range')}</TableCell>
                                <TableCell className="darker" colSpan={2}>
                                    {i18n('light_recipe')}
                                </TableCell>
                                <TableCell colSpan={2}>{i18n('spectrum_mix')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {spectrumOrder.map((name) => (
                                <TableRow key={name}>
                                    <TableCell>
                                        {i18n(unit === 'pfd' && name === 'visibleSum' ? 'detail_ppfd' : 'detail_' + name)}
                                        <br />
                                        <Typography variant="body2" style={{ fontSize: '12px' }}>
                                            {rangesMap[name]}
                                        </Typography>
                                    </TableCell>
                                    <TableCell className="darker bold">{recipeRanges[name]}</TableCell>
                                    <TableCell className="darker small">
                                        {recipeRanges.sum > 0 ? Math.round((recipeRanges[name] / recipeRanges.sum) * 100) : 0}%
                                    </TableCell>
                                    <TableCell className="bold">{mixRanges[name]}</TableCell>
                                    <TableCell className="small">{mixRanges.sum > 0 ? Math.round((mixRanges[name] / mixRanges.sum) * 100) : 0}%</TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell>{i18n('detail_red_blue_ratio')}</TableCell>
                                <TableCell className="darker center" colSpan={2}>
                                    {recipeRanges.blue > 0 ? (recipeRanges.red / recipeRanges.blue).toFixed(3) : '-'}
                                </TableCell>
                                <TableCell className="center" colSpan={2}>
                                    {mixRanges.blue > 0 ? (mixRanges.red / mixRanges.blue).toFixed(3) : '-'}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>{i18n('spectrum_mix_similarity')}</TableCell>
                                <TableCell className="darker" colSpan={2} />
                                <TableCell className="center" colSpan={2}>
                                    {similarity === null ? '-' : `${similarity}%`}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Root>
        </Grid>
    );
};

export default React.memo(SpectrumMixerDetailedIntensityInfo);
